<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none">
			<div class="inventory-actions">
				<v-btn depressed outlined @click="showPreview = !showPreview">Preview Chart</v-btn>
				<v-btn
					depressed
					outlined
					@click="duplicateSizeChart()"
					:disabled="duplicateActive || chartData.sizeChartId == null"
					v-if="canManageSizeCharts"
					>Duplicate</v-btn
				>
				<v-btn
					depressed
					outlined
					:disabled="chartData.sizeChartId == null"
					@click="deleteWarning = !deleteWarning"
					v-if="canManageSizeCharts && !hasDependentCharts"
					>Delete</v-btn
				>
				<v-tooltip bottom v-else-if="canManageSizeCharts">
					<template v-slot:activator="{ on, attrs }">
						<span v-on="on" v-bind="attrs">
							<v-btn
								depressed
								outlined
								:disabled="chartData.sizeChartId == null || hasDependentCharts"
								@click="deleteWarning = !deleteWarning"
								>Delete</v-btn
							>
						</span>
					</template>
					<span style="white-space: pre-line">{{ dependencyTooltip }}</span>
				</v-tooltip>
				<v-btn depressed outlined @click="cancelAndClearState">Cancel</v-btn>
				<v-btn
					depressed
					color="success"
					@click="shouldShowDialog ? (confirmDialog = true) : saveSizeChart()"
					v-if="canManageSizeCharts"
					>Save</v-btn
				>
			</div>
		</v-col>

		<v-col cols="6" class="flex-auto mx-auto">
			<div class="inventory-form">
				<div class="form-input">
					<label>Size Chart Name</label>
					<v-text-field
						:disabled="!canManageSizeCharts"
						outlined
						dense
						hide-details="auto"
						v-model="chartData.name"
					></v-text-field>
				</div>

				<div class="form-input">
					<label>Description</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="chartData.description"
						:disabled="!canManageSizeCharts"
					></v-text-field>
				</div>

				<div class="form-radio">
					<label>Include Length Sizes</label>
					<v-radio-group v-model="chartData.includeLengths" row hide-details="auto">
						<v-radio :disabled="!canManageSizeCharts" label="Yes" :value="true"></v-radio>
						<v-radio :disabled="!canManageSizeCharts" label="No" :value="false"></v-radio>
					</v-radio-group>
				</div>
			</div>

			<div class="inventory-sizes">
				<div class="inventory-sizes-description">
					<p v-if="chartData.includeLengths">
						Standard lengths should end with * (e.g. 12", 13"*, 14")
					</p>
					<p><em>Deleting a size will NOT affect any past orders</em></p>
				</div>
				<div
					class="inventory-sizes-table"
					:class="chartData.includeLengths ? null : 'inventory-sizes-table-2'"
				>
					<div v-if="loading" class="loading-overlay">
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
					</div>
					<div class="flex-table" role="table-head">
						<div class="flex-table-row" role="row-group">
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
							<div class="flex-table-cell" role="column-header">Size</div>
							<div v-if="chartData.includeLengths" class="flex-table-cell" role="column-header">
								Length
							</div>
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
						</div>
					</div>
					<draggable
						v-model="visibleSizes"
						tag="div"
						class="flex-table"
						role="table-body"
						handle=".drag-handle"
						:disabled="!canManageSizeCharts"
					>
						<div
							v-for="(data, i) in visibleSizes"
							:key="data.sizeId || data.name || i"
							class="flex-table-row"
							role="row-group"
						>
							<div class="flex-table-cell" role="cell">
								<v-icon
									v-if="canManageSizeCharts"
									class="drag-handle"
									style="cursor: move; font-size: 2rem"
									>mdi-drag</v-icon
								>
								<span v-else>{{ i + 1 }}</span>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="data.name"
										:disabled="!canManageSizeCharts"
									></v-text-field>
								</div>
							</div>
							<div v-if="chartData.includeLengths" class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="data.lengths"
										:disabled="!canManageSizeCharts"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<v-btn
									:disabled="!canManageSizeCharts"
									icon
									color="primary"
									@click="removeSizeRow(data)"
									><v-icon style="font-size: 2rem">mdi-delete</v-icon>
								</v-btn>
							</div>
						</div>
					</draggable>
				</div>
				<div class="inventory-sizes-actions">
					<v-btn v-if="canManageSizeCharts" text @click="addSizeRow()"
						><v-icon>mdi-plus</v-icon> Add a Row</v-btn
					>
				</div>
			</div>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete this size chart?'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deleteSizeChart();
				deleteWarning = false;
			"
		/>

		<SizingChart
			v-if="showPreview"
			:showSizingChart="showPreview"
			:modal="false"
			:sizes="chartData.sizes"
			:includeLengths="chartData.includeLengths"
			@cancel="showPreview = false"
		/>

		<v-dialog v-if="confirmDialog" v-model="confirmDialog" persistent width="1200">
			<v-card>
				<v-card-title class="text-h5 mb-2">
					Select the style codes in which you would like new sizes to be enabled.
				</v-card-title>
				<v-card-text>
					<div class="inventory-sizes-table-dialog">
						<div class="flex-table" role="table-head">
							<div class="flex-table-row" role="row-group">
								<div class="flex-table-cell" role="column-header">
									<v-checkbox v-model="selectAll" @change="toggleSelectAll" />
								</div>
								<div class="flex-table-cell" role="column-header">&nbsp;</div>
								<div class="flex-table-cell" role="column-header">Style Code</div>
								<div class="flex-table-cell" role="column-header">Description</div>
							</div>
						</div>
						<div class="flex-table" role="table-body">
							<div
								v-for="(data, i) in associatedSizeCharts"
								:key="i"
								class="flex-table-row"
								role="row-group"
							>
								<div class="flex-table-cell" role="cell">
									<v-checkbox v-model="data.selected" />
								</div>
								<div class="flex-table-cell" role="cell">
									<SystemCategoryIcon :system-category-id="data.systemCategoryId" />
								</div>
								<div class="flex-table-cell" role="cell">
									<div class="form-input">
										<span>{{ data.styleCode }}</span>
									</div>
								</div>
								<div class="flex-table-cell" role="cell">
									<div class="form-input">
										<span>{{ data.itemDescription }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn
						text
						@click="
							() => {
								this.confirmDialog = false;
							}
						"
					>
						Cancel
					</v-btn>
					<v-btn
						color="green darken-1"
						text
						@click="
							() => {
								this.confirmDialog = false;
								this.saveSizeChart();
							}
						"
					>
						Save
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import SizingChart from "@/components/SizingChart.vue";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import { renderPrice } from "@/helpers/PricingCalculator";
import { hasPermission, hasAnyPermission, SystemPermissions } from "@/models/User";
import { getApiUrl } from "@/helpers";
import draggable from "vuedraggable";

export default {
	name: "SizeChartView",
	props: {
		sizeChartId: {
			type: Number,
		},
	},
	components: {
		SizingChart,
		WarningModal,
		SystemCategoryIcon,
		draggable,
	},
	data() {
		return {
			chartData: {
				sizeChartId: 0,
				name: "",
				description: "",
				includeLengths: true,
				sizes: [
					{
						name: "",
						lengths: "",
						showSize: true,
					},
				],
			},
			loading: false,
			duplicateActive: false,
			showPreview: false,
			deleteWarning: false,
			originalChartData: {},
			confirmDialog: false,
			selectAll: true,
		};
	},
	apollo: {
		associatedSizeCharts: {
			query() {
				return gql`
					query GetAssociatedSizeChartItems($templateSizeChartId: Int!) {
						associatedSizeChartItems(templateSizeChartId: $templateSizeChartId) {
							sizeChartId
							templateSizeChartId
							name
							description
							isTemplate
							includeLengths
							inventoryItemId
							systemCategoryId
							styleCode
							itemDescription
						}
					}
				`;
			},
			fetchPolicy: "no-cache",
			variables() {
				const id = this.sizeChartId ?? this.chartData.sizeChartId;
				return {
					templateSizeChartId: id,
				};
			},
			update: (data) => {
				data.associatedSizeChartItems.forEach((item) => {
					item.selected = true;
				});
				return data.associatedSizeChartItems;
			},
		},
		dependentSizeCharts: {
			query() {
				return gql`
					query GetDependentSizeCharts($templateSizeChartId: Int!) {
						dependentSizeCharts(templateSizeChartId: $templateSizeChartId) {
							sizeChartId
							name
							description
							isTemplate
							styleCode
							itemDescription
						}
					}
				`;
			},
			fetchPolicy: "no-cache",
			variables() {
				const id = this.sizeChartId ?? this.chartData.sizeChartId;
				return {
					templateSizeChartId: id,
				};
			},
			skip() {
				// Skip this query if no valid ID is available
				const id = this.sizeChartId ?? this.chartData.sizeChartId;
				return !id || id <= 0;
			},
		},
	},
	methods: {
		formatDate(date) {
			if (!date) return "";

			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		formatRadioLabel(keyword) {
			return this.column === (keyword === "disable" ? 0 : 1)
				? `Apply changes to existing style codes and <b>${keyword}</b> any new size/color`
				: `Apply changes to existing style codes and ${keyword} any new size/color`;
		},

		renderLengthString(lengthsArr) {
			let lengthString = "";
			//loop through length array and get each size and compile into one string
			lengthsArr.forEach((len, i) => {
				if (i !== lengthsArr.length - 1) {
					lengthString += `${len.length}, `;
				} else {
					lengthString += `${len.length}`;
				}
			});

			return lengthString;
		},

		renderPrice,

		addSizeRow() {
			const maxSeq = this.chartData.sizes.reduce(
				(max, s) => Math.max(max, s.sequence ?? 0),
				0
			);
			let newSize = {
				name: "",
				lengths: "",
				showSize: true,
				sequence: maxSeq + 1,
			};

			this.chartData.sizes.push(newSize);
		},

		removeSizeRow(rowData) {
			//filter out the passed in rowData
			this.chartData.sizes = this.chartData.sizes.filter((obj) => obj !== rowData);
		},

		duplicateSizeChart() {
			//remove the sizeChart id so we can save a new copy and replace the name
			this.chartData.sizeChartId = null;
			this.chartData.name = `${this.chartData.name} (DUPLICATE)`;

			//set duplcate state
			this.duplicateActive = true;
		},

		async deleteSizeChart() {
			try {
				const res = await this.$http.delete(
					getApiUrl(`/api/settings/size-chart/${this.sizeChartId ?? this.chartData.sizeChartId}`)
				);
				console.log(res);

				this.$router.push("/inventory/size-charts");
			} catch (error) {
				console.log(error);
			}
		},

		async saveSizeChart() {
			try {
				this.loading = true;
				let sizeData;
				let res;

				if (this.chartData.sizeChartId) {
					sizeData = {
						...this.chartData,
						sizes: this.chartData.sizes.map((obj) => {
							const sizeObj = {
								name: obj.name,
								showSize: obj.showSize,
								lengths: this.chartData.includeLengths ? obj.lengths : "",
								sizeId: obj.sizeId,
								isRetired: obj.isRetired,
								sequence: obj.sequence,
							};
							return sizeObj;
						}),
						associations: this.associatedSizeCharts,
					};
					res = await this.$http.put(
						getApiUrl("/api/settings/size-chart"),
						sizeData
					);
				} else {
					sizeData = {
						...this.chartData,
						sizes: this.chartData.sizes.map((obj) => {
							const sizeObj = {
								name: obj.name,
								showSize: obj.showSize,
								lengths: obj.lengths,
								isRetired: obj.isRetired,
								sequence: obj.sequence,
							};
							return sizeObj;
						}),
					};
					res = await this.$http.post(
						getApiUrl("/api/settings/size-chart"),
						sizeData
					);
				}
				console.log(res);
				this.$router.push("/inventory/size-charts");
			} catch (error) {
				console.log(error);
			} finally {
				this.loading = false;
			}
		},

		isEqual(obj1, obj2) {
			return JSON.stringify(obj1) === JSON.stringify(obj2);
		},

		toggleSelectAll() {
			this.associatedSizeCharts.forEach((item) => {
				item.selected = this.selectAll;
			});
		},

		cancelAndClearState() {
			// Clear the saved state for size charts list to prevent auto-navigation issues
			this.$store.dispatch("globalState/saveSizeChartsState", {
				offset: 0,
				keywords: "",
				scrollPosition: 0,
			});

			// Navigate back to list
			this.$router.push("/inventory/size-charts");
		},

		async getSizeChart() {
			try {
				const res = await this.$apollo.query({
					query: gql`
						query GetSizeChart($sizeChartId: Int!) {
							sizeCharts(where: { sizeChartId: { eq: $sizeChartId }, isTemplate: { eq: true } }) {
								items {
									sizeChartId
									name
									description
									isTemplate
									includeLengths
									sizes {
										name
										lengths {
											length
											sizeId
											sizeLengthId
											isRetired
										}
										showSize
										isRetired
										sequence
									}
								}
							}
						}
					`,
					variables: {
						sizeChartId: this.sizeChartId,
					},
					fetchPolicy: "no-cache",
				});

				// Since we're querying by ID, we expect only one item
				const sizeChart = res.data.sizeCharts.items[0];
				if (sizeChart) {
					//reformat the chart data size lengths to work as strings
					this.chartData = {
						...sizeChart,
						sizes: sizeChart.sizes.map((obj) => ({
							sizeId: obj.lengths.length > 0 ? obj.lengths[0].sizeId : null,
							name: obj.name,
							showSize: obj.showSize,
							lengths: this.renderLengthString(obj.lengths.filter((l) => !l.isRetired)),
							isRetired: obj.isRetired,
							sequence: obj.sequence,
						})),
					};

					this.originalChartData = {
						...sizeChart,
						sizes: sizeChart.sizes.map((obj) => ({
							sizeId: obj.lengths.length > 0 ? obj.lengths[0].sizeId : null,
							name: obj.name,
							showSize: obj.showSize,
							lengths: this.renderLengthString(obj.lengths.filter((l) => !l.isRetired)),
							isRetired: obj.isRetired,
							sequence: obj.sequence,
						})),
					};
				}
			} catch (error) {
				console.error("Error fetching size chart:", error);
			}
		},
	},
	watch: {
		associatedSizeCharts: {
			deep: true,
			handler(newItems, oldItems) {
				this.selectAll = this.allItemsSelected;
			},
		},
	},
	computed: {
		visibleSizes: {
			get() {
				return this.chartData.sizes
					.filter((s) => !s.isRetired)
					.sort((a, b) => (a.sequence ?? 1000) - (b.sequence ?? 1000));
			},
			set(reordered) {
				reordered.forEach((size, i) => this.$set(size, "sequence", i + 1));
			},
		},
		canManageSizeCharts() {
			const user = this.$store.getters.userInfo;
			return (
				hasPermission(user, SystemPermissions.Inventory_SizeChart_Create_Update_Delete) ||
				user.isAdmin
			);
		},
		allItemsSelected() {
			if (!this.associatedSizeCharts || this.associatedSizeCharts.length === 0) return false;
			return this.associatedSizeCharts.every((item) => item.selected);
		},
		shouldShowDialog() {
			return this.associatedSizeCharts && this.associatedSizeCharts.length > 0;
		},
		hasDependentCharts() {
			return this.dependentSizeCharts && this.dependentSizeCharts.length > 0;
		},
		dependencyTooltip() {
			if (!this.hasDependentCharts) return "";

			const chartsList = this.dependentSizeCharts
				.map((sc) => (sc.styleCode !== "N/A" ? `${sc.name} (${sc.styleCode})` : sc.name))
				.join("\n");

			return `Cannot delete size chart - it is referenced by the following charts:\n${chartsList}`;
		},
	},
	async mounted() {
		if (this.sizeChartId) {
			await this.getSizeChart();
		}
	},
};
</script>

<style lang="scss" scoped>
.inventory {
	&-actions {
		display: flex;
		justify-content: flex-end;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			margin-left: 0.5rem;
		}
	}

	&-sizes {
		display: flex;
		flex-flow: column nowrap;
		height: 100%;
		margin-top: 2rem;

		&-description {
			p {
				color: $color-gray;
				font-size: 1.4rem;
			}
		}

		&-table {
			flex-grow: 1;
			background-color: $color-white;

			.flex-table-cell {
				.form-input {
					width: 100%;
				}

				&:nth-child(1) {
					width: 8%;
					justify-content: center;
				}
				&:nth-child(2) {
					width: 42%;
				}
				&:nth-child(3) {
					width: 42%;
				}
				&:nth-child(4) {
					width: 8%;
					justify-content: center;
				}
			}

			&-2 {
				.flex-table-cell {
					.form-input {
						width: 100%;
					}

					&:nth-child(1) {
						width: 8%;
						justify-content: center;
					}
					&:nth-child(2) {
						width: 84%;
					}
					&:nth-child(3) {
						width: 8%;
						justify-content: center;
					}
				}
			}
		}

		&-table-dialog {
			.flex-table-cell {
				.form-input {
					width: 100%;
				}

				&:nth-child(1) {
					width: 8%;
					justify-content: center;
				}
				&:nth-child(2) {
					width: 8%;
					justify-content: center;
				}
				&:nth-child(3) {
					width: 40%;
				}
				&:nth-child(4) {
					width: 40%;
				}
				&:nth-child(5) {
					width: 4%;
					justify-content: center;
				}
			}
		}

		&-actions {
			margin-top: 1rem;

			.v-btn {
				font-size: 1.4rem;
				padding-left: 1rem;

				.v-icon {
					font-size: 2rem;
					margin-right: 0.5rem;
				}
			}
		}
	}
}
</style>
